/**
 * useSalesInvoices — 매출전표(sales_invoices) 거래일 범위 실시간 구독.
 *
 * 날짜 셀렉트가 빠르게 바뀔 때 리스너가 즉시 재구독되면 Firestore SDK가
 * "INTERNAL ASSERTION FAILED"(target state race)를 던지므로 60ms 디바운스.
 * (useInbound 의 watchMaterialBetween 패턴과 동일)
 */
import { useEffect, useMemo, useState } from "react";
import {
  salesRepo,
  salesPrefsRepo,
  DEFAULT_SALES_PREFS,
  type SalesPrefs,
  type SalesSyncStatus,
} from "@/services/sales.repository";
import type { SalesAllocation, SalesBalance, SalesInvoice, SalesReceipt, SalesRep } from "@/domain/sales";

export function useSalesInvoicesBetween(from: string, to: string) {
  const [data, setData] = useState<SalesInvoice[]>([]);
  const [loading, setLoading] = useState<boolean>(!!from && !!to);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!from || !to) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let unsub: (() => void) | null = null;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      unsub = salesRepo.watchInvoicesBetween(
        from,
        to,
        (rows) => {
          if (cancelled) return;
          setData(rows);
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (cancelled) return;
          setError(err);
          setLoading(false);
        },
      );
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (unsub) unsub();
    };
  }, [from, to]);

  return { data, loading, error };
}

/** 수금전표(sales_receipts) 수금일 범위 실시간 구독 (useSalesInvoicesBetween 와 동일 패턴) */
export function useSalesReceiptsBetween(from: string, to: string) {
  const [data, setData] = useState<SalesReceipt[]>([]);
  const [loading, setLoading] = useState<boolean>(!!from && !!to);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!from || !to) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let unsub: (() => void) | null = null;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      unsub = salesRepo.watchReceiptsBetween(
        from,
        to,
        (rows) => {
          if (cancelled) return;
          setData(rows);
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (cancelled) return;
          setError(err);
          setLoading(false);
        },
      );
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (unsub) unsub();
    };
  }, [from, to]);

  return { data, loading, error };
}

/** 담당자 마스터 1회 로드 (멀티선택 옵션 소스) */
export function useSalesReps() {
  const [data, setData] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    salesRepo
      .fetchReps()
      .then((reps) => {
        if (alive) {
          setData(reps);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { data, loading };
}

/**
 * 거래처 미수 스냅샷 1회 로드 → 거래처코드별 Map 반환.
 * 미수는 기준일(as_of) 시점 누계잔액이라 조회 기간과 무관(항상 표시).
 */
export function useSalesBalances(refreshKey = 0) {
  const [data, setData] = useState<SalesBalance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    salesRepo
      .fetchBalances(refreshKey > 0)
      .then((b) => {
        if (alive) {
          setData(b);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const byCode = useMemo(() => {
    const m = new Map<string, SalesBalance>();
    for (const b of data) m.set(b.customerCode, b);
    return m;
  }, [data]);

  const asOf = data[0]?.asOf ?? "";
  return { data, byCode, asOf, loading };
}

/**
 * 관급 현장별 잔량(배정−출하) 1회 로드 → 거래처코드별 Map.
 * 잔량은 배정 있는 관급/관공서 거래처(~14곳)만 존재.
 */
export function useSalesAllocations(refreshKey = 0) {
  const [data, setData] = useState<SalesAllocation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    salesRepo
      .fetchAllocations(refreshKey > 0)
      .then((a) => {
        if (alive) {
          setData(a);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  const byCode = useMemo(() => {
    const m = new Map<string, SalesAllocation>();
    for (const a of data) m.set(a.customerCode, a);
    return m;
  }, [data]);

  return { data, byCode, loading };
}

/** 마지막 동기화 상태 구독 (sales_sync_status/latest). enabled=false면 구독 안 함(권한/설정 게이팅용). */
export function useSalesSyncStatus(enabled = true) {
  const [status, setStatus] = useState<SalesSyncStatus | null>(null);
  useEffect(() => {
    if (!enabled) { setStatus(null); return; }
    return salesRepo.watchSyncStatus(setStatus);
  }, [enabled]);
  return status;
}

/** 개인 표시 설정 (기본 보기/조회기간) 구독 + 저장 */
export function useSalesPrefs() {
  const [prefs, setPrefs] = useState<SalesPrefs>(DEFAULT_SALES_PREFS);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => salesPrefsRepo.watch((p) => { setPrefs(p); setLoaded(true); }), []);
  return { prefs, loaded, save: salesPrefsRepo.save };
}
